import type { Track } from '@/world/Track';
import { Rng } from '@/core/Rng';
import {
  aiCarParamsFor,
  aiCarParamsRubber,
  botBallPushFor,
  vehicleCarParamsFor,
  vehicleWeightFor,
  vehicleBallPushFor,
  vehicleBoxFor,
  possessionParamsFor,
  tuning,
  type DifficultyLevel,
} from '@/config/tuning';
import { defaultVehicleForSlot } from '@/config/racers';
import { SimRacer } from '@/sim/SimRacer';
import { RACER_POOL } from '@/config/racers';
import type { CarInput } from '@/physics/arcadeCar';
import { forwardX, forwardZ } from '@/math/orientation';
import {
  resolveBallCar,
  resolveCarCar,
  resolveCarClash,
  resolveBallBall,
  resolveDozerHit,
  resolveWall,
  resolveFreeKickWall,
  deflectFreeKickWall,
  magnetHold,
  kidAssistPull,
  possessionPull,
  kickBall,
  type ClashHit,
  type FreeKickWallShape,
} from '@/physics/collisions';
import {
  placePickups,
  stepPickups,
  resetPickups,
  tryCollect,
  nearestPickupAhead,
  type PickupSpot,
} from '@/physics/pickups';
import { decideDrive, type DriverContext } from '@/ai/Driver';
import {
  stepPowerups,
  collectItem,
  useItem,
  rollItem,
  autoGrantScorpion,
  SCORPION_MAX,
  canCollect,
  canUse,
  isActive,
  type PowerupKind,
} from '@/physics/powerups';
import { rampHeight, rampSlope, rideRamp } from '@/physics/ramp';
import {
  placeFeatures,
  inStrip,
  inFordWater,
  fordParamsFor,
  resolveObstacle,
  slickParamsFor,
  type PlacedFeature,
} from '@/physics/trackFeatures';
import { placeMovers, resolveMover, moverPoseAt, type PlacedMover } from '@/physics/movers';
import {
  placeShortcuts,
  mouthCross,
  deckCatch,
  kickerHeight,
  kickerForwardSpeed,
  type PlacedShortcut,
  type CrossingBody,
} from '@/physics/shortcuts';
import { CAR_RADIUS } from '@/physics/collisions';
import type { RampSpec, CourseExtras } from '@/config/cities';

export type RaceState = 'countdown' | 'racing' | 'finished';

const COUNTDOWN_SECONDS = 3;
const GO_FLASH_SECONDS = 0.7;

/**
 * One racer's control intent, in WORLD space (heading-relative `+` increases
 * heading). The screen→world steer flip (§6.2) is a CLIENT concern applied
 * before the input reaches here, so a human frame and an AI `DriverOutput` are
 * the identical shape to the sim — the seam that lets a bot fill an empty slot
 * or take over a dropped human with no special-casing.
 */
export interface RacerInput {
  throttle: number;
  steer: number;
  /** Edge: fire the held item on this tick. */
  useItem: boolean;
}

/** A fire-able item that produces a one-shot sound/flash (tackle is separate). */
export type FireKind =
  | 'boost'
  | 'magnet'
  | 'dozer'
  | 'shield'
  | 'kick'
  | 'wall'
  | 'offside'
  | 'pibe'
  | 'studs' // M59: spikes out
  | 'scorpion' // M59: the flip-kick
  | 'mud'; // M59: puddle dropped

/**
 * Side-effects the sim used to fire inline (audio + the player's screen-shake/
 * toast hooks) become DATA: the SimCore appends them to `events` each step and
 * the client drains them into `sfx*()` + `RaceFx`. The server drains the same
 * buffer to build its reliable event stream. Every event carries the racer it
 * belongs to, so each client can decide which are "mine".
 */
export type SimEvent =
  | { type: 'countdown'; go: boolean }
  | { type: 'pad'; racerId: number } // M99: hit a boost pad (entry kick landed)
  | { type: 'itemLand'; racerId: number }
  | { type: 'collect'; racerId: number }
  | { type: 'fire'; racerId: number; kind: FireKind }
  | { type: 'deny'; racerId: number }
  | { type: 'ballHit'; racerId: number; intensity: number }
  | { type: 'dozerHit'; victimId: number }
  | { type: 'shieldBlock' }
  | { type: 'tackleLaunch'; color: number; targetId: number }
  | { type: 'tackleHit'; victimId: number }
  | { type: 'offsideHit'; victimId: number } // M46: caught by an OFFSIDE TRAP
  | { type: 'pibeHit'; victimId: number } // M46: smashed aside by a PIBE DE ORO runner
  | { type: 'clash'; victimId: number; tier: 'spin' | 'stagger' } // M57: weight-disparity hit
  | { type: 'wallBreak' } // M46: a free-kick wall shattered (pibe ran through it)
  | { type: 'studsHit'; victimId: number } // M59: a rival spun out on the STUDS OUT spikes
  | { type: 'shortcut'; racerId: number } // M116: threaded a SECRET SHORTCUT gate
  | { type: 'finish'; racerId: number; order: number; timeMs: number };

/** The live slide-tackle ghost (M14): chase up the course, then the slide. */
export interface TackleState {
  firerId: number;
  targetId: number;
  /** Ghost position on the course (arc-length + lateral). */
  dist: number;
  lat: number;
  phase: 'chase' | 'slide';
  timer: number;
  heading: number;
}

/** A planted FREE KICK WALL (M46): frozen in world space where it was dropped.
 *  Extends the pure collision shape with ownership + lifetime + render info. */
export interface WallDrop extends FreeKickWallShape {
  ownerId: number;
  /** Firer's colour, so the client can tint the wall. */
  color: number;
  /** Ground height at the drop point (for rendering). */
  y: number;
  /** Seconds left standing. */
  timer: number;
}

/** A dropped SLIPPERY MUD puddle (M59): a circle on the ground that slows every
 *  RIVAL car + ball crossing it (grip loss + a velocity bleed). Pure data —
 *  SimCore owns the list + physics, the client (MudView) draws it. */
export interface MudDrop {
  ownerId: number;
  x: number;
  y: number;
  z: number;
  radius: number;
  color: number;
  /** Seconds left before the puddle dries up. */
  timer: number;
}

/**
 * SimCore.ts — the PURE, three.js-free authoritative race simulation: the field
 * of racers, the 3-2-1-GO countdown, the full collision set (every ball vs every
 * car, car-car, ball-ball), the item engine, the slide-tackle ghost, finish
 * detection, and standings ranked by BALL progress (not car position).
 *
 * It steps from a MAP of per-racer inputs: a racer present in the map is driven
 * by that input (a human); a racer absent from it is driven by the AI Driver.
 * Offline single-player passes one entry (the local player); the online server
 * passes one per connected human and lets the AI fill the rest. Nothing here
 * renders or plays audio — those come out as `events`.
 *
 * (Extracted from core/Race.ts in the M18 headless split; the logic is the same
 * code that shipped through M17b, so the AI pace tests still pin it.)
 */
export class SimCore {
  racers: SimRacer[] = [];
  state: RaceState = 'countdown';
  countdown = COUNTDOWN_SECONDS;
  goFlash = 0;
  /** Seconds since GO (the race clock — stops when the player finishes). */
  raceTime = 0;

  /** The item boxes on the course (pure state; PickupsView draws them). */
  pickups: PickupSpot[] = [];

  /** Drained by the caller after each `step` (cleared at the start of the next). */
  readonly events: SimEvent[] = [];

  /** The live slide-tackle ghost, or null — the client renders it. */
  tackle: TackleState | null = null;

  /** Planted free-kick walls (M46) — pure state; the client renders them. */
  walls: WallDrop[] = [];

  /** Dropped SLIPPERY MUD puddles (M59) — pure state; the client renders them. */
  mud: MudDrop[] = [];

  /** The control each racer used on the LAST step (world-space). The server
   *  broadcasts these so a client can predict EVERY racer forward, not just its
   *  own — the basis of Rocket-League-style full-world client prediction. */
  readonly lastInputs = new Map<number, RacerInput>();

  track: Track;
  private difficulty: DifficultyLevel;
  private ramp: RampSpec | undefined;
  /** Interactive course features (M47) resolved into world space — static per
   *  track, so they never need snapshotting. */
  features: PlacedFeature[] = [];
  /** Moving obstacles (M99) — the TIMETABLE is static per track; each pose is
   *  a pure function of the snapshotted `raceTime`, so these never need
   *  snapshotting either (physics/movers.ts determinism contract). */
  movers: PlacedMover[] = [];
  /** Secret shortcuts (M117) — drivable channels + their wall openings,
   *  frozen per track (never snapshotted); who is INSIDE one rides the
   *  per-racer synced carChannel/ballChannel indices. */
  shortcuts: PlacedShortcut[] = [];
  private seed: number;
  fieldSize: number;
  /** Which racer slots are human-occupied (stock engine); the rest are AI bots.
   *  Default [0] = the single-player / loopback case (slot 0 is you). */
  private humanSlots: number[];

  /** Pick a racer's vehicle (M51 — vehicles are REAL in every mode). Offline
   *  the Race sets slot 0 from the car-select; online the ROOM sets each
   *  human's choice (from its join URL) + randomizes the AI slots, and
   *  snapshots carry the truth to every client's prediction sim. */
  setVehicle(slot: number, vehicleId: string): void {
    const r = this.racers.find((x) => x.id === slot);
    if (r) r.vehicleId = vehicleId;
  }

  /** Convenience for the offline Race: the local player is always slot 0. */
  setPlayerVehicle(vehicleId: string | undefined): void {
    if (vehicleId) this.setVehicle(this.player.id, vehicleId);
  }

  /** The engine/handling params a racer drives this step. HUMANS get their
   *  vehicle's full stat profile (M49/M51); BOTS keep the level-scaled AI
   *  engine whatever vehicle they APPEAR in — the locked pace ladder and the
   *  M17 recovery maneuvers are tuned around it. (A bot's vehicle weight IS
   *  real in car-car shoves though — see weightOf.) */
  /** Pace reference captured once at the top of each racing step (M77) — the
   *  engine catch-up derives each bot's gap from it, purely per call, so a
   *  client predicting from any snapshot computes identical params. */
  private stepPaceRef = 0;

  /** M143c: obstacles that leave no room to pass on their wall side (a cab
   *  double-parked by the rail), as hazards the AI steers around: the blocked
   *  strip runs from the obstacle's inner edge all the way to the rail, so the
   *  only clear side is the road. Static per track — measured once. (The
   *  physics already refuses to wedge a body in such a pocket — see
   *  resolveObstacle — but a car that drives straight at one still stops dead
   *  behind it and has to work its way round.) */
  private pocketCache: {
    of: PlacedFeature[];
    list: { ownerId: number; arc: number; lateral: number; halfSpan: number }[];
  } | null = null;
  private pocketHazards(): { ownerId: number; arc: number; lateral: number; halfSpan: number }[] {
    if (this.pocketCache?.of === this.features) return this.pocketCache.list;
    const list: { ownerId: number; arc: number; lateral: number; halfSpan: number }[] = [];
    const hw = this.track.halfWidth;
    for (const f of this.features) {
      if (f.kind !== 'obstacle' || f.wallGap >= 2 * CAR_RADIUS) continue;
      const p = this.track.project(f.x, f.z);
      const inner = Math.abs(p.lateral) - f.radius; // the obstacle's road-side edge
      const side = Math.sign(p.lateral) || 1;
      list.push({
        ownerId: -1,
        arc: p.distance,
        lateral: (side * (inner + hw)) / 2,
        halfSpan: (hw - inner) / 2,
      });
    }
    this.pocketCache = { of: this.features, list };
    return list;
  }

  /** Which end of a planted free-kick wall stands against the guardrail with
   *  no room for a ball to pass: +1 / -1 (the wall's own right / left), 0 when
   *  both ends are open (M143c, see deflectFreeKickWall). A wall never moves,
   *  so the answer is cached per wall object; a snapshot brings fresh objects
   *  and the answer is simply worked out again from the same position. */
  private wallEnds = new WeakMap<WallDrop, number>();
  private wallBlockedEnd(w: WallDrop): number {
    let end = this.wallEnds.get(w);
    if (end === undefined) {
      const lat = this.track.project(w.x, w.z).lateral;
      const gap = this.track.halfWidth - (Math.abs(lat) + w.halfSpan);
      end = gap < 2 * tuning.ball.radius ? Math.sign(lat) || 1 : 0;
      this.wallEnds.set(w, end);
    }
    return end;
  }

  private carParamsFor(r: SimRacer): typeof tuning.car {
    if (r.isBot) {
      // M77: Legend's rubber lives in the ENGINE (catchUpEngine) — the cruise
      // rubber alone was measured impotent there (cruise 55 already exceeds
      // the carScale-1.1 drag ceiling, so a higher target changed nothing).
      // The rocket engages only AWAY from the ball (ferrying back / chasing a
      // punt); in the strike zone the stock engine keeps the approach clean —
      // extra accel fighting the approach cap was the measured M16 thrash.
      const c = r.car.state;
      const b = r.ball.state;
      if (Math.hypot(c.x - b.x, c.z - b.z) < tuning.ai.catchUpEngageDist) {
        return aiCarParamsFor(this.difficulty);
      }
      return aiCarParamsRubber(this.difficulty, this.stepPaceRef - r.ballDistance(this.track));
    }
    return vehicleCarParamsFor(r.vehicleId);
  }

  /** A racer's collision weight (M49/M51): car-car shove factor is 1/weight,
   *  for EVERYONE — when the heavy monster truck meets a light bike, the bike
   *  gets knocked by exactly the disparity (owner ask: physics match the
   *  visuals in every mode, AI included). */
  private weightOf(r: SimRacer): number {
    return vehicleWeightFor(r.vehicleId);
  }

  /** PIBE DE ORO's ball-push params (M46): a golden runner BLASTS rival balls
   *  out of its path instead of dribbling them. Same for humans and bots so the
   *  sim stays deterministic across prediction. */
  private readonly pibeBallPush = {
    ...tuning.ball,
    carPush: tuning.ball.carPush * tuning.powerups.pibe.ballBlast,
  };

  private finishCount = 0;
  private itemRng: Rng;
  private lastShieldBlock = -1;

  constructor(
    track: Track,
    difficulty: DifficultyLevel,
    course: CourseExtras | undefined,
    seed: number,
    fieldSize: number,
    humanSlots: number[] = [0],
  ) {
    this.track = track;
    this.difficulty = difficulty;
    this.ramp = course?.ramp;
    this.features = course?.features ? placeFeatures(track, course.features) : [];
    this.movers = course?.movers ? placeMovers(track, course.movers) : [];
    this.shortcuts = course?.shortcuts
      ? placeShortcuts(track, course.shortcuts, course.ramp)
      : [];
    this.seed = seed;
    this.fieldSize = fieldSize;
    this.humanSlots = humanSlots;
    this.buildField();
    this.itemRng = new Rng(seed ^ 0x51ab);
    this.placeBoxes();
  }

  /** (Re)build the racer field: player + N rivals, lanes from the grid slots,
   *  per-rival pace from the field's skill spread (aces + backmarkers). Every
   *  racer starts on its default vehicle (M51): slot 0 the hero sports car,
   *  rivals cycling the roster — the offline look, and the online placeholder
   *  until the room broadcasts the real choices via snapshots. */
  private buildField(): void {
    const n = Math.min(this.fieldSize, RACER_POOL.length, tuning.racers.laneSlots.length);
    const skills = tuning.racers.fieldSkill[n - 1] ?? [];
    this.racers = RACER_POOL.slice(0, n).map((identity, i) => {
      const lane = tuning.racers.laneSlots[i];
      const paceScale = identity.isPlayer ? 1 : (skills[i - 1] ?? 1);
      const isBot = !this.humanSlots.includes(i);
      const r = new SimRacer(identity, this.track, lane, paceScale, isBot);
      r.vehicleId = defaultVehicleForSlot(i);
      return r;
    });
  }

  /** Re-target the race to a new city's track + difficulty (and field size),
   *  and reset back to the countdown. Racers rebuild only if the field changed. */
  loadTrack(
    track: Track,
    difficulty: DifficultyLevel,
    course: CourseExtras | undefined,
    seed: number,
    fieldSize: number,
  ): void {
    this.track = track;
    this.difficulty = difficulty;
    this.ramp = course?.ramp;
    this.features = course?.features ? placeFeatures(track, course.features) : [];
    this.movers = course?.movers ? placeMovers(track, course.movers) : [];
    this.shortcuts = course?.shortcuts
      ? placeShortcuts(track, course.shortcuts, course.ramp)
      : [];
    this.seed = seed;
    if (fieldSize !== this.fieldSize) {
      this.fieldSize = fieldSize;
      this.buildField();
    }
    this.placeBoxes();
    this.reset();
  }

  /** Lay out the item boxes for the current track (deterministic per seed). */
  private placeBoxes(): void {
    const ramp = this.ramp;
    this.pickups = placePickups(
      this.track,
      this.seed,
      tuning.powerups.boxes,
      ramp ? { start: ramp.at * this.track.finishDistance, length: ramp.length } : undefined,
    );
  }

  get player(): SimRacer {
    const p = this.racers.find((r) => r.isPlayer);
    if (!p) throw new Error('SimCore has no player racer');
    return p;
  }

  /** The pace reference the competitive layer (AI rubber-band, desperation
   *  items) measures against: the furthest-along HUMAN ball. Offline that IS
   *  the player (slot 0 is the only human), byte-identical to the old
   *  player-based reference; online it's the leading human, so catch-up centres
   *  on the people in the race, not on whoever happens to hold slot 0 (M45). */
  private paceReference(): number {
    return this.leadingHuman().ballDistance(this.track);
  }

  /** The furthest-along HUMAN racer — the competitive layer's reference, and
   *  (M77) the harass target whose ball a Legend rival may swipe in passing. */
  private leadingHuman(): SimRacer {
    let best: SimRacer | null = null;
    let bestDist = -Infinity;
    for (const r of this.racers) {
      if (r.isBot) continue;
      const d = r.ballDistance(this.track);
      if (d > bestDist) {
        bestDist = d;
        best = r;
      }
    }
    return best ?? this.player;
  }

  reset(): void {
    for (const r of this.racers) r.reset(this.track);
    this.state = 'countdown';
    this.countdown = COUNTDOWN_SECONDS;
    this.goFlash = 0;
    this.raceTime = 0;
    this.finishCount = 0;
    this.lastShieldBlock = -1;
    this.tackle = null;
    this.walls.length = 0;
    this.mud.length = 0;
    this.itemRng = new Rng(this.seed ^ 0x51ab); // same race → same item rolls
    resetPickups(this.pickups);
    this.events.length = 0;
  }

  /**
   * Advance the authoritative sim one fixed step. `inputs` maps racerId →
   * world-space control for every HUMAN-driven racer; any racer not in the map
   * is driven by the AI Driver. Appends side-effects to `events` (cleared here
   * at the top of every step, so drain after each call).
   */
  step(inputs: Map<number, RacerInput>, dt: number): void {
    this.events.length = 0;
    if (this.state === 'finished') return;

    if (this.state === 'countdown') {
      const prevTick = Math.ceil(this.countdown);
      this.countdown -= dt;
      if (this.countdown <= 0) {
        this.state = 'racing';
        this.goFlash = GO_FLASH_SECONDS;
        this.events.push({ type: 'countdown', go: true }); // GO!
      } else if (Math.ceil(this.countdown) !== prevTick) {
        this.events.push({ type: 'countdown', go: false }); // 3… 2… 1…
      }
      return; // engines idle on the line until GO
    }

    if (this.goFlash > 0) this.goFlash -= dt;
    this.raceTime += dt;

    this.stepMud(dt); // M59: expire dried-up puddles before the loop reads them

    const leadHuman = this.leadingHuman();
    const playerDist = leadHuman.ballDistance(this.track);
    this.stepPaceRef = playerDist;
    // Car positions along the track (for the AI's dozer/shield/box senses).
    const carProj = this.racers.map((rr) =>
      this.track.project(rr.car.state.x, rr.car.state.z, rr.car.hint),
    );
    // M77 hazard sense: project each planted mud puddle / free-kick wall onto
    // the course ONCE per step; each Legend AI reads the nearest one in its
    // lane ahead and steers around it (Driver `avoidHazards`). A handful of
    // objects at most — the projections are cheap next to the AI's own.
    let hazards: { ownerId: number; arc: number; lateral: number; halfSpan: number }[] | null =
      null;
    const senseMovers =
      this.movers.length > 0 && tuning.ai.levels[this.difficulty].avoidHazards;
    // M143c: …and the obstacles parked too close to a rail to pass behind.
    const pockets = tuning.ai.levels[this.difficulty].avoidHazards ? this.pocketHazards() : [];
    if (this.mud.length > 0 || this.walls.length > 0 || senseMovers || pockets.length > 0) {
      hazards = [];
      for (const h of pockets) hazards.push(h);
      // NO hint here (M99 fix): project() with a hint only searches a small
      // window around it — hint 0 CLAMPED any hazard beyond ~240 u to a bogus
      // early-course arc, silently feeding the Legend avoid-steer phantom
      // positions. A hint-less call scans the whole track (a handful of
      // objects at most — still cheap).
      for (const m of this.mud) {
        const p = this.track.project(m.x, m.z);
        hazards.push({ ownerId: m.ownerId, arc: p.distance, lateral: p.lateral, halfSpan: m.radius });
      }
      for (const w of this.walls) {
        const p = this.track.project(w.x, w.z);
        hazards.push({ ownerId: w.ownerId, arc: p.distance, lateral: p.lateral, halfSpan: w.halfSpan });
      }
      // M104: `avoidHazards` levels finally SEE the M99 crossing traffic. A
      // mover's pose is a pure function of the snapshotted race clock, so the
      // predicted lateral (~0.7 s ahead — about the time a bot takes to reach
      // a hazard inside its lookahead) is deterministic on every client. A
      // mover parked in its kerb dwell still gets fed — its lateral sits by
      // the rail, far off any aim point, so the dodge is a natural no-op.
      if (senseMovers) {
        for (const m of this.movers) {
          const pose = moverPoseAt(m, this.raceTime + 0.7);
          const p = this.track.project(m.x, m.z);
          hazards.push({
            ownerId: -1,
            arc: p.distance,
            lateral: p.lateral + pose.lat,
            halfSpan: m.halfLen + m.radius + 1,
          });
        }
      }
    }
    // M77 harass sense: where the leading human's ball sits (Legend rivals
    // behind that human may swipe it off-line in passing).
    const preyState = leadHuman.ball.state;
    // (M117: a lead ball inside a shortcut channel is off the road — projecting
    // it would feed the harass sense a bogus arc, so the prey goes dark.)
    const preyOnRoad = leadHuman.ballChannel < 0;
    const preyProj = preyOnRoad
      ? this.track.project(preyState.x, preyState.z, leadHuman.ball.hint)
      : null;
    for (let ri = 0; ri < this.racers.length; ri++) {
      const r = this.racers[ri];
      // Tick this racer's item slot + any bulldozer stun every step.
      const rouletteDone = stepPowerups(r.powerups, dt);
      if (rouletteDone) this.events.push({ type: 'itemLand', racerId: r.id });
      if (r.stunTimer > 0) r.stunTimer = Math.max(0, r.stunTimer - dt);
      // M60: the ball-flight immunity counts down independently of the car flip.
      if (r.scorpionBallTimer > 0) r.scorpionBallTimer = Math.max(0, r.scorpionBallTimer - dt);

      // SCORPION KICK (M59/M60b): while the car is LOCKED it holds its spot —
      // no control, zero velocity, no integration. Phase 1 (timer above
      // flipTime) the ball rises straight up; the instant it crosses into
      // phase 2 (timer ≤ flipTime = the apex) the car VOLLEYS the ball high +
      // far down-course. Its ball keeps flying untouchable AFTER the car lands
      // too, gated by scorpionBallTimer. Driven by the synced timers, so every
      // client resolves it identically.
      if (r.scorpionTimer > 0) {
        const flipTime = tuning.powerups.scorpion.flipTime;
        const before = r.scorpionTimer;
        r.scorpionTimer = Math.max(0, before - dt);
        if (before > flipTime && r.scorpionTimer <= flipTime) {
          // Apex reached → the scorpion volley (forward + a big upward kick).
          const sc = tuning.powerups.scorpion;
          const s = this.track.sampleAt(
            this.track.project(r.car.state.x, r.car.state.z, r.car.hint).distance,
          );
          const b = r.ball.state;
          b.vx = Math.sin(s.dir) * sc.forwardSpeed; // §6.1 forward (sin h, cos h)
          b.vz = Math.cos(s.dir) * sc.forwardSpeed;
          b.vy = sc.upSpeed;
        }
        r.car.state.vx = 0;
        r.car.state.vz = 0;
        this.lastInputs.set(r.id, { throttle: 0, steer: 0, useItem: false });
        r.ball.update(dt, this.track);
        continue;
      }

      // SECRET SHORTCUT entry (M117): BEFORE this racer's physics runs, check
      // whether its car or ball is about to thread a doorway (pre-move
      // kinematics — the road wall clamp must never fire for a threaded body).
      // Humans only: openings are solid wall for bots and bot balls, so the
      // locked pace ladder is untouched by construction.
      if (this.shortcuts.length > 0 && !r.isBot && !r.finished) {
        this.tryChannelEntry(r, carProj[ri], dt);
      }

      const human = inputs.get(r.id);
      let ci: CarInput;
      if (r.stunTimer > 0) {
        // Rammed by a bulldozer: no control, spin out while the launch carries it.
        ci = { throttle: 0, steer: r.stunSteer, boosting: false };
        // Stun is driven by stunTimer (which the client has from the snapshot), so
        // the recorded input is moot — a client re-derives the spin identically.
        this.lastInputs.set(r.id, { throttle: 0, steer: 0, useItem: false });
      } else if (human) {
        // Human-driven (local player offline; a connected player online).
        if (human.useItem) this.fireItem(r);
        // Steer arrives already in world space; boost flag from the shared engine.
        ci = {
          throttle: human.throttle,
          steer: human.steer,
          boosting: isActive(r.powerups, 'boost'),
        };
        this.lastInputs.set(r.id, {
          throttle: human.throttle,
          steer: human.steer,
          useItem: human.useItem,
        });
      } else {
        // AI rival: the Driver plans in world space and asks to fire its held
        // item through the same fireItem path as the player's button.
        const i = ri;
        let rivalAhead: { gap: number; lateral: number } | undefined;
        let rivalBehind: { gap: number; lateral: number } | undefined;
        for (let j = 0; j < this.racers.length; j++) {
          if (j === i || this.racers[j].finished) continue;
          if (this.racers[j].carChannel >= 0) continue; // M117: cars inside a channel aren't traffic
          const dAlong = carProj[j].distance - carProj[i].distance;
          const lateral = carProj[j].lateral - carProj[i].lateral;
          if (dAlong > 0 && dAlong < 10 && (!rivalAhead || dAlong < rivalAhead.gap)) {
            rivalAhead = { gap: dAlong, lateral };
          }
          if (dAlong < 0 && dAlong > -10 && (!rivalBehind || -dAlong < rivalBehind.gap)) {
            rivalBehind = { gap: -dAlong, lateral };
          }
        }
        const myDist = r.ballDistance(this.track);
        // M77: nearest rival hazard ahead in this car's lane (skip my own drops).
        let hazardAhead: { ahead: number; lateral: number; halfSpan: number } | null = null;
        if (hazards) {
          for (const h of hazards) {
            if (h.ownerId === r.id) continue;
            const ahead = h.arc - carProj[i].distance;
            if (ahead <= 0 || ahead >= tuning.ai.hazard.lookahead) continue;
            if (!hazardAhead || ahead < hazardAhead.ahead) {
              hazardAhead = { ahead, lateral: h.lateral, halfSpan: h.halfSpan };
            }
          }
        }
        // M77: the leading human's ball, when this racer trails that human and
        // the ball sits just ahead of its car (a passing-swipe opportunity).
        const preyAhead = preyProj ? preyProj.distance - carProj[i].distance : 0;
        const preyBall =
          preyProj && !leadHuman.finished && myDist < playerDist && preyAhead > 0 && preyAhead < tuning.ai.harass.range
            ? { x: preyState.x, z: preyState.z, ahead: preyAhead, lateral: preyProj.lateral }
            : null;
        const ctx: DriverContext = {
          trailingPlayer: myDist < playerDist,
          gapToPlayer: playerDist - myDist,
          rivalAhead,
          rivalBehind,
          boxAhead: nearestPickupAhead(
            this.pickups,
            carProj[i].distance,
            tuning.ai.items.boxSeekAhead,
          ),
          hazardAhead,
          preyBall,
          // M104: a live boost-pad surge is sim state the Driver can't read
          // off its powerup slot — flag it so the speed controller cashes it.
          boosted: r.padBoost > 0,
          difficulty: this.difficulty,
          paceScale: r.paceScale,
          // M143c: the AI covering a dropped PLAYER's seat drives that
          // player's engine; tell the driver so its brake and reverse commands
          // are scaled to it. The scaled command is what gets recorded and
          // broadcast, so every client applies the same one.
          cover: r.isBot ? undefined : vehicleCarParamsFor(r.vehicleId),
        };
        const out = decideDrive(
          this.track,
          r.car.state,
          r.ball.state,
          r.ball.hint,
          r.car.hint,
          r.driverMemory,
          r.powerups,
          ctx,
          dt,
        );
        if (out.useItem) this.fireItem(r);
        ci = out;
        this.lastInputs.set(r.id, { throttle: out.throttle, steer: out.steer, useItem: out.useItem });
      }
      // PIBE DE ORO (M46): the golden runner's engine runs at boost speed for
      // the whole effect — one chokepoint for humans AND bots alike.
      if (isActive(r.powerups, 'pibe') && !ci.boosting) ci = { ...ci, boosting: true };
      // Human-driven racers drive the stock (or M49 vehicle) engine; AI rivals
      // drive their level-scaled engine (M15). Keyed off identity, so a bot
      // that takes over a dropped human switches cleanly.
      let params = this.carParamsFor(r);
      // Course features (M47): a BOOST pad refreshes the engine surge while
      // crossed; a SLICK patch swaps in low-grip params for exactly the steps
      // spent on it (memoized — no per-step allocation). Road furniture: a car
      // inside a shortcut channel (M117) is off the road, so none apply.
      let inFord = false;
      if (!r.finished && r.carChannel < 0 && this.features.length > 0) {
        const c = r.car.state;
        let onSlick = false;
        for (const f of this.features) {
          if (f.kind === 'ford') {
            // nyc-lake: open water across the road. The dry PLANK window is
            // exempt, so the reward for holding the line is simply not paying.
            if (!inFord && inFordWater(c.x, c.z, f)) inFord = true;
            continue;
          }
          if (f.kind === 'boost') {
            if (inStrip(c.x, c.z, f)) {
              // M99 (owner: pads didn't feel like a big speed change): pad
              // ENTRY lands an instant forward kick on top of the surge.
              // Entry = padBoost expired at contact — a SNAPSHOTTED gate, so
              // online prediction re-derives the same kick from any snapshot
              // (no hidden per-step state). Riding the pad keeps refreshing
              // the surge without re-kicking; a re-cross after the surge
              // fades kicks again.
              if (r.padBoost <= 0) {
                const fx = Math.sin(c.heading);
                const fz = Math.cos(c.heading);
                const vf = c.vx * fx + c.vz * fz;
                const add = Math.min(
                  tuning.features.padKick,
                  Math.max(0, params.boostTop - vf),
                );
                c.vx += fx * add;
                c.vz += fz * add;
                this.events.push({ type: 'pad', racerId: r.id });
              }
              r.padBoost = tuning.features.boostTime;
            }
          } else if (f.kind === 'slick' && !onSlick && inStrip(c.x, c.z, f)) {
            onSlick = true;
          }
        }
        if (onSlick) params = slickParamsFor(params);
        // Wading beats a slick if a ford ever overlaps one (nothing does today).
        if (inFord) params = fordParamsFor(params);
      }
      // SLIPPERY MUD (M59): a rival's puddle robs grip (slick params now) and
      // bleeds speed (after the move, below). The owner's own mud is harmless.
      const inMud = !r.finished && this.carInRivalMud(r);
      if (inMud) params = slickParamsFor(params);
      if (r.padBoost > 0) {
        r.padBoost = Math.max(0, r.padBoost - dt);
        if (!ci.boosting) ci = { ...ci, boosting: true };
      }
      // M117: an entity inside a shortcut channel is updated against the
      // CHANNEL's Track — its floor profile and bore walls replace the road's,
      // and the player simply DRIVES the tunnel (full control, real physics).
      if (!r.finished) {
        r.car.update(dt, ci, this.carTrackOf(r), params, vehicleBoxFor(r.vehicleId));
        if (inMud) {
          const f = Math.max(0, 1 - tuning.powerups.mud.carDrag * dt);
          r.car.state.vx *= f;
          r.car.state.vz *= f;
        }
        if (inFord) {
          // The wading cost. Applied AFTER the move, like mud, so the engine
          // still fights the water instead of the drag silently eating the
          // throttle — you crawl across, you never stop dead.
          const f = Math.max(0, 1 - tuning.features.ford.carDrag * dt);
          r.car.state.vx *= f;
          r.car.state.vz *= f;
        }
      } else {
        // M42: a finished car COASTS through the line into the run-out (drag
        // brings it to rest well clear of the finish) instead of freezing in
        // place — a frozen car right on the line was a wall for every ball and
        // car still racing. Stateless (throttle 0 forever), so online clients
        // re-derive it identically from the `finished` flag alone.
        r.car.update(
          dt,
          { throttle: 0, steer: 0, boosting: false },
          this.carTrackOf(r),
          params,
          vehicleBoxFor(r.vehicleId),
        );
      }
      r.ball.update(dt, this.ballTrackOf(r));
      // SLIPPERY MUD (M59): a rival's ball rolling through the puddle bleeds
      // speed too ("slow down and lose traction"). The owner's ball is spared.
      if (this.ballInRivalMud(r)) {
        const f = Math.max(0, 1 - tuning.powerups.mud.ballDrag * dt);
        r.ball.state.vx *= f;
        r.ball.state.vz *= f;
      }
      // nyc-lake THE FORD: a ball wide of the plank wades too — the owner's
      // spec is explicit that the plank carries "the drivers AND their balls",
      // and standings rank by ball progress, so this is the half that matters.
      this.applyFordToBall(r, dt);
      this.applyStartApron(r, dt); // M143c: a ball left in the start box rolls back out
      // M117: track channel progress + hand control back at the exit stub
      // (also refreshes the standings lerp fields for an in-channel ball).
      this.updateChannelState(r);
    }

    this.resolveCollisions(dt);
    this.applyObstacles();
    this.applyMovers(); // M99: crossing city traffic (deterministic timetable)
    this.applyWalls(dt);
    this.applyStuds(); // M59: spikes deflect rival balls into the guardrails
    this.applyPossessionAssist(dt); // M84: everyone's soft dribble touch (after knocks, so a hit this tick sheds it)
    this.applyKidAssist(dt); // M83: Big Kid's soft ball leash (before the hard magnet cradle)
    this.applyMagnets();
    this.applyLaunchMagnet(dt); // M117c: the launch ramps' temporary ball-carry (after the item cradle, which outranks it)
    this.applyRamp();
    this.applyKickers(); // M117: the jump shortcuts' launch ramps (physical, for everyone)
    // M117c: the launch magnet (above) MOVES in-channel balls after their
    // channel progress was refreshed — re-derive now so ballDistance (and a
    // freshly adopted snapshot) reads the FINAL position, not one nudge ago.
    for (const r of this.racers) {
      if (r.ballChannel >= 0) this.refreshBallChannel(r);
    }
    this.updatePickups(dt);
    this.updateTackle(dt);
    this.updateFinish();
  }

  /** The Track a racer's CAR physically drives this step: the road, or the
   *  shortcut channel it has threaded into (M117). */
  private carTrackOf(r: SimRacer): Track {
    return r.carChannel >= 0 ? (this.shortcuts[r.carChannel]?.channel ?? this.track) : this.track;
  }

  /** Same for the BALL — car and ball thread doorways independently. */
  private ballTrackOf(r: SimRacer): Track {
    return r.ballChannel >= 0
      ? (this.shortcuts[r.ballChannel]?.channel ?? this.track)
      : this.track;
  }

  /** SECRET SHORTCUT catch (M117c): the FLIGHT is the gate. A wall-aperture
   *  style (cavern mouth / highline edge) catches a body whose flight line
   *  pierces the wall inside the span at or above the sill (mouthCross); the
   *  skypath's overhead deck catches a body crossing its plane high enough
   *  (deckCatch) — road traffic passes underneath untouched. Car and ball
   *  are judged independently, with pre-move kinematics so the road clamp
   *  never fires for a caught body. The 'shortcut' event (gold toast +
   *  chime) celebrates the CAR's catch. */
  private tryChannelEntry(
    r: SimRacer,
    carProj: { distance: number; lateral: number },
    dt: number,
  ): void {
    if (r.stunTimer > 0 || r.scorpionTimer > 0) return;
    const hw = this.track.halfWidth;
    const car = r.car.state;
    // The human car's own wall-clamp line (Car.ts: halfWidth − CAR_HALF_WIDTH
    // − wallMargin). Entry is human-only, so the human limit is the limit.
    const carLimit = hw - 1.0 - tuning.car.wallMargin;
    for (let i = 0; i < this.shortcuts.length; i++) {
      const sc = this.shortcuts[i];
      const ax = sc.catch?.x ?? sc.door.x;
      const az = sc.catch?.z ?? sc.door.z;
      if (r.carChannel < 0 && Math.abs(car.x - ax) + Math.abs(car.z - az) < 45) {
        const body: CrossingBody = {
          x: car.x,
          z: car.z,
          vx: car.vx,
          vz: car.vz,
          y: r.car.y,
          vy: r.car.vy,
          arc: carProj.distance,
          lateral: carProj.lateral,
          radius: 1.1,
          clampLimit: carLimit,
        };
        if (sc.catch ? deckCatch(sc, body, dt) : mouthCross(sc, body, dt)) {
          r.carChannel = i;
          r.car.hint = 0; // hints are per-track — reset on every space switch
          this.events.push({ type: 'shortcut', racerId: r.id });
        }
      }
      if (
        r.ballChannel < 0 &&
        r.scorpionBallTimer <= 0 &&
        Math.abs(r.ball.state.x - ax) + Math.abs(r.ball.state.z - az) < 45
      ) {
        const b = r.ball.state;
        const proj = this.track.project(b.x, b.z, r.ball.hint);
        const body: CrossingBody = {
          x: b.x,
          z: b.z,
          vx: b.vx,
          vz: b.vz,
          y: b.y,
          vy: b.vy,
          arc: proj.distance,
          lateral: proj.lateral,
          radius: b.radius,
          clampLimit: hw - b.radius,
        };
        if (sc.catch ? deckCatch(sc, body, dt) : mouthCross(sc, body, dt)) {
          r.ballChannel = i;
          r.ball.hint = 0;
          this.refreshBallChannel(r); // standings continuity from this very step
        }
      }
    }
  }

  /** Track channel progress after the physics ran: refresh the in-channel
   *  ball's standings lerp, and hand an entity back to the road once it
   *  reaches the exit stub (which pokes INSIDE the road, so the switch is
   *  position-continuous — no teleports, ever). */
  private updateChannelState(r: SimRacer): void {
    if (r.carChannel >= 0) {
      const sc = this.shortcuts[r.carChannel];
      if (!sc) {
        r.carChannel = -1;
      } else {
        const p = sc.channel.project(r.car.state.x, r.car.state.z, r.car.hint);
        r.car.hint = p.index;
        // BACK-OUT (M117c, owner rule): a racer who landed WITHOUT their ball
        // reverses off the deck's leading edge and simply drops back to the
        // road below — the entry is open both ways.
        const n0 = sc.channel.nodes[0];
        const backing =
          r.car.state.vx * Math.sin(n0.dir) + r.car.state.vz * Math.cos(n0.dir) < -0.5;
        if (p.distance < 2.5 && backing) {
          r.carChannel = -1;
          r.car.hint = this.roadHintAt(sc.entryArc);
          r.car.airborne = true; // FALL home — never snap down to road grade
          r.car.vy = Math.min(r.car.vy, 0);
          // A road-ward shove so an over-the-wall deck edge drops you INSIDE
          // the course instead of onto the verge clamp.
          r.car.state.vx += -sc.door.side * sc.door.rightX * 6;
          r.car.state.vz += -sc.door.side * sc.door.rightZ * 6;
        } else if (p.distance >= sc.leaveDist) {
          r.carChannel = -1;
          r.car.hint = this.roadHintAt(sc.exitArc);
        }
      }
    }
    if (r.ballChannel >= 0) {
      const sc = this.shortcuts[r.ballChannel];
      if (!sc) {
        r.ballChannel = -1;
        r.chLen = 0;
        r.ballChDist = 0;
      } else {
        this.refreshBallChannel(r);
        const n0 = sc.channel.nodes[0];
        const b = r.ball.state;
        const ballBacking = b.vx * Math.sin(n0.dir) + b.vz * Math.cos(n0.dir) < -0.5;
        if (r.ballChDist + sc.doorDist < 2.5 && ballBacking) {
          // The ball rolls back off the deck edge and drops home too.
          r.ballChannel = -1;
          r.ball.hint = this.roadHintAt(sc.entryArc);
          b.vx += -sc.door.side * sc.door.rightX * 4;
          b.vz += -sc.door.side * sc.door.rightZ * 4;
          r.chLen = 0;
          r.ballChDist = 0;
        } else if (r.ballChDist + sc.doorDist >= sc.leaveDist) {
          r.ballChannel = -1;
          r.ball.hint = this.roadHintAt(sc.exitArc);
          r.chLen = 0;
          r.ballChDist = 0;
        }
      }
    }
  }

  private roadHintAt(arc: number): number {
    return Math.max(0, Math.min(this.track.nodes.length - 2, Math.round(arc / this.track.step)));
  }

  /** Refill an in-channel ball's derived standings fields (chFrom/chTo/chLen/
   *  ballChDist) from its REAL projected progress along the channel — pure in
   *  synced state, so server + every predicting client agree. The lerp runs
   *  door → exit-hand-off, both measured, so the road hand-off is seamless. */
  private refreshBallChannel(r: SimRacer): void {
    const sc = this.shortcuts[r.ballChannel];
    if (!sc) return;
    const p = sc.channel.project(r.ball.state.x, r.ball.state.z, r.ball.hint);
    r.ball.hint = p.index;
    r.chFrom = sc.entryArc;
    r.chTo = sc.exitHandArc;
    r.chLen = Math.max(1, sc.leaveDist - sc.doorDist);
    r.ballChDist = p.distance - sc.doorDist;
  }

  /** Refill every racer's derived channel fields after a snapshot adoption
   *  (the wire carries only the two channel indices; the lerp fields derive
   *  from placed geometry + the synced ball position). An index this sim
   *  can't resolve bails safely back to the road. */
  refreshChannelDerived(): void {
    for (const r of this.racers) {
      if (r.carChannel >= 0 && !this.shortcuts[r.carChannel]) r.carChannel = -1;
      if (r.ballChannel >= 0) {
        if (!this.shortcuts[r.ballChannel]) {
          r.ballChannel = -1;
          r.chLen = 0;
          r.ballChDist = 0;
        } else {
          this.refreshBallChannel(r);
        }
      } else {
        r.chLen = 0;
      }
    }
  }

  /** The jump shortcuts' KICKER ramps (M117): physical objects on the road
   *  verge, applied to EVERY body like the city launch ramp — a bot that
   *  strays onto one gets the hop (and thuds; the catch itself stays shut to
   *  bots). Aimed at the landing, so driving straight over the kicker lines
   *  the flight up — accuracy is being on its line. HUMANS riding a launch
   *  ramp also collect the launch aids (M117c): a speed surge + the
   *  temporary ball magnet, so committed jumps carry the ball. */
  private applyKickers(): void {
    for (const sc of this.shortcuts) {
      const k = sc.kicker;
      if (!k) continue;
      const slope = k.height / k.length;
      for (const r of this.racers) {
        if (r.finished) continue;
        if (r.carChannel < 0 && r.scorpionTimer <= 0) {
          const c = r.car;
          const h = kickerHeight(k, c.state.x, c.state.z);
          if (h > 0) {
            if (!r.isBot) this.grantLaunchAids(r);
            const gy = this.track.heightAt(c.state.x, c.state.z, c.hint);
            rideRamp(c, gy + h, kickerForwardSpeed(k, c.state.vx, c.state.vz), slope);
            c.airborne = true;
          }
        }
        if (r.ballChannel < 0 && r.scorpionBallTimer <= 0) {
          const b = r.ball.state;
          const h = kickerHeight(k, b.x, b.z);
          if (h > 0) {
            const gy = this.track.heightAt(b.x, b.z, r.ball.hint);
            rideRamp(b, gy + b.radius + h, kickerForwardSpeed(k, b.vx, b.vz), slope);
          }
        }
      }
    }
  }

  /** M117c launch aids, granted while a HUMAN rides a shortcut launch ramp:
   *  one instant speed kick + an engine surge (the ramp's "boost pad") and
   *  the temporary ball magnet. Bots never get them — they can never enter a
   *  shortcut, so the pace ladder never feels the surge. Fresh contact is
   *  detected by the magnet timer being empty (the M99 padBoost pattern:
   *  synced state, so online prediction re-derives the same kick). */
  private grantLaunchAids(r: SimRacer): void {
    const cfg = tuning.shortcuts;
    if (r.launchMagnet <= 0) {
      const c = r.car.state;
      const fx = Math.sin(c.heading);
      const fz = Math.cos(c.heading);
      const vf = c.vx * fx + c.vz * fz;
      const params = this.carParamsFor(r);
      const add = Math.min(cfg.launchKick, Math.max(0, params.boostTop - vf));
      c.vx += fx * add;
      c.vz += fz * add;
      this.events.push({ type: 'pad', racerId: r.id });
    }
    r.padBoost = Math.max(r.padBoost, cfg.launchBoostTime);
    r.launchMagnet = cfg.launchMagnetTime;
  }

  /** M117c: the launch ramps' TEMPORARY MAGNET — while it runs, the ball
   *  rides the nose in full 3D (position AND height), so car + ball fly the
   *  jump together and land together. The real magnet/pibe cradle outranks
   *  it; a flying scorpion ball stays untouchable; the carry only works when
   *  car and ball share a space (a wall between them is real). */
  private applyLaunchMagnet(dt: number): void {
    for (const r of this.racers) {
      if (r.launchMagnet <= 0) continue;
      r.launchMagnet = Math.max(0, r.launchMagnet - dt);
      if (r.finished || r.scorpionBallTimer > 0 || r.stunTimer > 0) continue;
      if (isActive(r.powerups, 'magnet') || isActive(r.powerups, 'pibe')) continue;
      if (r.carChannel !== r.ballChannel) continue;
      const ball = r.ball.state;
      // Range-gated: the carry keeps a dribble TOGETHER — it must never yank
      // a ball from across the road onto the nose (magnetHold pins position).
      const d = Math.hypot(r.car.state.x - ball.x, r.car.state.z - ball.z);
      if (d > tuning.shortcuts.launchMagnetRange) continue;
      magnetHold(ball, r.car.state, tuning.powerups.magnetBallGap, vehicleBoxFor(r.vehicleId));
      ball.y = r.car.y + ball.radius + 0.15;
      ball.vy = r.car.vy;
    }
  }

  /** Tick + expire dropped SLIPPERY MUD puddles (M59). */
  private stepMud(dt: number): void {
    for (let i = this.mud.length - 1; i >= 0; i--) {
      this.mud[i].timer -= dt;
      if (this.mud[i].timer <= 0) this.mud.splice(i, 1);
    }
  }

  /** Is this racer's CAR sitting on a RIVAL's mud puddle (M59)? */
  private carInRivalMud(r: SimRacer): boolean {
    if (r.carChannel >= 0) return false; // M117: mud is road furniture — a car under it (in a bore) can't touch it
    for (const m of this.mud) {
      if (m.ownerId === r.id) continue;
      const dx = r.car.state.x - m.x;
      const dz = r.car.state.z - m.z;
      if (dx * dx + dz * dz <= m.radius * m.radius) return true;
    }
    return false;
  }

  /** Is this racer's BALL rolling through a RIVAL's mud puddle (M59)? */
  private ballInRivalMud(r: SimRacer): boolean {
    if (r.ballChannel >= 0) return false; // M117: road furniture (see carInRivalMud)
    for (const m of this.mud) {
      if (m.ownerId === r.id) continue;
      const dx = r.ball.state.x - m.x;
      const dz = r.ball.state.z - m.z;
      if (dx * dx + dz * dz <= m.radius * m.radius) return true;
    }
    return false;
  }

  /**
   * nyc-lake THE FORD, ball side. A ball wide of the plank bleeds speed like a
   * car does — and then the WATER CARRIES IT, which is the load-bearing half.
   *
   * WHY THE CURRENT EXISTS: drag alone is a trap. `v *= (1 - k·dt)` can only
   * ever asymptote toward zero, so a ball that arrives slowly (or is knocked
   * sideways into the water by a rival) parks in the ford permanently, and
   * since standings rank by BALL progress that is a race-ending hazard, not a
   * cost. The lake FLOWS under the bridge (the owner's own framing), so inside
   * the water the ball's down-course component is held at `current`: every
   * ball in a ford leaves it in bounded time, by construction. Pinned in
   * tests/physics/ford.test.ts across the whole entry-speed range.
   *
   * Pure function of position + the ford's frozen frame — no per-step state,
   * so online prediction re-derives it identically from any snapshot.
   */
  private applyFordToBall(r: SimRacer, dt: number): void {
    if (r.ballChannel >= 0 || this.features.length === 0) return; // road furniture (see ballInRivalMud)
    if (isActive(r.powerups, 'scorpion')) return; // a scorpion ball is in FLIGHT, not in the water
    const b = r.ball.state;
    for (const f of this.features) {
      if (f.kind !== 'ford' || !inFordWater(b.x, b.z, f)) continue;
      const cfg = tuning.features.ford;
      const drag = Math.max(0, 1 - cfg.ballDrag * dt);
      b.vx *= drag;
      b.vz *= drag;
      const along = b.vx * f.dirX + b.vz * f.dirZ;
      if (along < cfg.current) {
        const add = Math.min(cfg.current - along, cfg.currentAccel * dt);
        b.vx += f.dirX * add;
        b.vz += f.dirZ * add;
      }
      return; // one ford at a time (they never overlap)
    }
  }

  /**
   * THE START APRON (M143c, see tuning.track.startApron): a grounded ball
   * resting in the start box, or just past the line, is carried down-course
   * until a car can get behind it. Like the ford's current it is a pure
   * function of the ball's position and the track's fixed start frame, with no
   * per-step state, so online prediction re-derives it from any snapshot.
   */
  private applyStartApron(r: SimRacer, dt: number): void {
    if (r.ballChannel >= 0 || r.scorpionBallTimer > 0 || r.finished) return;
    const cfg = tuning.track.startApron;
    const s = this.track.start;
    const b = r.ball.state;
    const fx = forwardX(s.dir);
    const fz = forwardZ(s.dir);
    const past = (b.x - s.x) * fx + (b.z - s.z) * fz;
    if (past >= cfg.reach || past < -tuning.track.startBarrier - b.radius) return;
    // The start line's plane runs on for ever; only the box itself counts.
    const across = (b.x - s.x) * s.rightX + (b.z - s.z) * s.rightZ;
    if (Math.abs(across) > this.track.halfWidth) return;
    // In flight over the box (a punt, a scorpion landing): leave it alone.
    if (b.y > this.track.heightAt(b.x, b.z, r.ball.hint) + b.radius + 0.5) return;
    const along = b.vx * fx + b.vz * fz;
    if (along < cfg.current) {
      const add = Math.min(cfg.current - along, cfg.accel * dt);
      b.vx += fx * add;
      b.vz += fz * add;
    }
  }

  /** Static OBSTACLES (M47, M51 diverter rework): parked taxis / rocks /
   *  crates near the road edges. Cars and balls SLIDE AROUND them
   *  tangentially, keeping most of their speed — they interrupt your line,
   *  never your race. City furniture — the same for everyone (even a Pibe de
   *  Oro runner respects a parked taxi). */
  private applyObstacles(): void {
    if (this.features.length === 0) return;
    for (const f of this.features) {
      if (f.kind !== 'obstacle') continue;
      for (const r of this.racers) {
        const car = r.car.state;
        if (!r.finished && r.scorpionTimer <= 0 && r.carChannel < 0) {
          // (M117: a car inside a channel is off the road — judged per ENTITY,
          // so a road ball still feels obstacles while its car is underground)
          // (a flipping car is intangible — M59)
          const hit = resolveObstacle(
            car.x,
            car.z,
            CAR_RADIUS,
            f,
            car.vx,
            car.vz,
            tuning.features.obstacleCarKeep,
          );
          if (hit) {
            car.x += hit.pushX;
            car.z += hit.pushZ;
            car.vx = hit.vx;
            car.vz = hit.vz;
          }
        }
        if (r.scorpionBallTimer > 0) continue; // a flying scorpion ball is untouchable (M60)
        if (r.ballChannel >= 0) continue; // M117: a channel ball is off the road
        const ball = r.ball.state;
        const bh = resolveObstacle(
          ball.x,
          ball.z,
          ball.radius,
          f,
          ball.vx,
          ball.vz,
          tuning.features.obstacleBallKeep,
        );
        if (bh) {
          ball.x += bh.pushX;
          ball.z += bh.pushZ;
          ball.vx = bh.vx;
          ball.vz = bh.vz;
        }
      }
    }
  }

  /** Moving obstacles (M99): city traffic shuttling across the road on a fixed
   *  timetable — a pure function of the snapshotted race clock, so offline and
   *  every online client agree for free. Same diverter doctrine as the static
   *  obstacles (slide around, keep most speed), plus the mover's own motion
   *  shoves a body it rolls into instead of grinding over it. */
  private applyMovers(): void {
    if (this.movers.length === 0) return;
    const t = this.raceTime;
    for (const m of this.movers) {
      for (const r of this.racers) {
        const car = r.car.state;
        if (!r.finished && r.scorpionTimer <= 0 && r.carChannel < 0) {
          // (a flipping car is intangible — M59; a channel car is off the road — M117)
          const hit = resolveMover(
            car.x,
            car.z,
            CAR_RADIUS,
            m,
            t,
            car.vx,
            car.vz,
            tuning.features.moverCarKeep,
          );
          if (hit) {
            car.x += hit.pushX;
            car.z += hit.pushZ;
            car.vx = hit.vx;
            car.vz = hit.vz;
          }
        }
        if (r.scorpionBallTimer > 0) continue; // a flying scorpion ball is untouchable (M60)
        if (r.ballChannel >= 0) continue; // M117: a channel ball is off the road
        const ball = r.ball.state;
        const bh = resolveMover(
          ball.x,
          ball.z,
          ball.radius,
          m,
          t,
          ball.vx,
          ball.vz,
          tuning.features.moverBallKeep,
        );
        if (bh) {
          ball.x += bh.pushX;
          ball.z += bh.pushZ;
          ball.vx = bh.vx;
          ball.vz = bh.vz;
        }
      }
    }
  }

  /** Tick + collide the planted free-kick walls (M46): cars bump off them
   *  (never the owner's own car), balls rebound (including the owner's — that's
   *  the defensive point), and a PIBE DE ORO runner shatters them on contact. */
  private applyWalls(dt: number): void {
    if (this.walls.length === 0) return;
    const pw = tuning.powerups.wall;
    for (let i = this.walls.length - 1; i >= 0; i--) {
      const w = this.walls[i];
      w.timer -= dt;
      if (w.timer <= 0) {
        this.walls.splice(i, 1);
        continue;
      }
      let broken = false;
      for (const r of this.racers) {
        const car = r.car.state;
        const ball = r.ball.state;
        const carGhost = r.scorpionTimer > 0 || r.carChannel >= 0; // flipping (M59) / off in a channel (M117)
        const ballGhost = r.scorpionBallTimer > 0 || r.ballChannel >= 0; // flying (M60) / off in a channel (M117)
        if (isActive(r.powerups, 'pibe')) {
          // The golden runner smashes straight through — car or ball contact.
          if (
            (!carGhost && resolveFreeKickWall(car.x, car.z, 1.5, w, car.vx, car.vz, 0)) ||
            (!ballGhost && resolveFreeKickWall(ball.x, ball.z, ball.radius, w, ball.vx, ball.vz, 0))
          ) {
            broken = true;
            break;
          }
          continue;
        }
        if (r.id !== w.ownerId && !r.finished && !carGhost) {
          const hit = resolveFreeKickWall(car.x, car.z, 1.5, w, car.vx, car.vz, pw.carRestitution);
          if (hit) {
            car.x += hit.pushX;
            car.z += hit.pushZ;
            car.vx = hit.vx;
            car.vz = hit.vz;
          }
        }
        // M51: balls are DEFLECTED sideways toward the guardrail (a diverter),
        // never fired back down-course — a wall costs you a moment, not a minute.
        if (ballGhost) continue; // the scorpion ball sails over the wall (M60)
        const bh = deflectFreeKickWall(
          ball.x,
          ball.z,
          ball.radius,
          w,
          ball.vx,
          ball.vz,
          pw.ballDeflectKeep,
          this.wallBlockedEnd(w),
        );
        if (bh) {
          ball.x += bh.pushX;
          ball.z += bh.pushZ;
          ball.vx = bh.vx;
          ball.vz = bh.vz;
        }
      }
      if (broken) {
        this.walls.splice(i, 1);
        this.events.push({ type: 'wallBreak' });
      }
    }
  }

  /** Advance the slide-tackle ghost: race up the course, home onto the leader's
   *  lane, then the slide — the victim is smacked into the nearest guardrail
   *  and briefly stunned (a raised SHIELD blocks it entirely). */
  private updateTackle(dt: number): void {
    const t = this.tackle;
    if (!t) return;
    t.timer += dt;
    const victim = this.racers.find((r) => r.id === t.targetId);
    if (!victim || victim.finished || t.timer > tuning.powerups.tackle.maxChase) {
      this.tackle = null;
      return;
    }
    const tk = tuning.powerups.tackle;
    if (t.phase === 'chase') {
      // M117: a victim mid-SHORTCUT is off the road AND unprojectable — the
      // ghost HOLDS STATION over the bore and takes them the moment they pop
      // out (the shortcut delays the reckoning, never cancels it; the chase
      // clock keeps running, so an eternal hide still dissolves the ghost).
      if (victim.carChannel >= 0) return;
      const vp = this.track.project(victim.car.state.x, victim.car.state.z, victim.car.hint);
      // Fly up the course (faster when the gap is huge so it always arrives),
      // sliding onto the victim's lane as it closes.
      const gap = vp.distance - t.dist;
      const speed = Math.max(tk.ghostSpeed, gap / 3);
      t.dist += speed * dt;
      t.lat += (vp.lateral - t.lat) * Math.min(tk.homing * dt, 1);
      const here = this.track.sampleAt(t.dist);
      t.heading = here.dir;
      if (t.dist >= vp.distance - 1.2) {
        // Contact. A raised shield clinks it away (and a PIBE DE ORO runner is
        // simply untouchable); otherwise — the tackle.
        if (isActive(victim.powerups, 'shield') || isActive(victim.powerups, 'pibe')) {
          this.events.push({ type: 'shieldBlock' });
          this.tackle = null;
          return;
        }
        const v = victim.car.state;
        const wallSign = vp.lateral >= 0 ? 1 : -1; // smack toward the NEAR wall
        const keepF = tk.keepForward;
        v.vx = vp.rightX * wallSign * tk.shove + v.vx * keepF;
        v.vz = vp.rightZ * wallSign * tk.shove + v.vz * keepF;
        victim.stunTimer = tk.stun;
        victim.stunSteer = wallSign * tuning.powerups.dozerSpinSteer;
        this.events.push({ type: 'tackleHit', victimId: victim.id });
        t.phase = 'slide';
        t.timer = 0;
        t.lat = vp.lateral; // slide through the victim's lane
      }
    } else {
      // The follow-through: the ghost sweeps on (decelerating) and dissolves.
      t.dist += tk.ghostSpeed * 0.3 * (1 - t.timer / tk.slideTime) * dt;
      if (t.timer >= tk.slideTime) {
        this.tackle = null;
      }
    }
  }

  /**
   * Fire a racer's held item — ONE code path for the player's button and the
   * AI's decision, so the rules can never drift apart. Magnet/Kick demand the
   * owner's ball within `ballRange` (≈3 car lengths); a too-far press keeps the
   * item and tells the player why.
   */
  private fireItem(r: SimRacer): void {
    const pw = tuning.powerups;
    if (!canUse(r.powerups)) return;
    const kind = r.powerups.held;
    // M117: some items make no sense INSIDE a shortcut channel — a wall/mud
    // plant would drop road furniture at an underground position, a scorpion
    // ball would sail through the bore roof, and the tackle ghost paths on
    // the road. Denied with the usual buzz; the item is KEPT for the exit.
    if (
      r.carChannel >= 0 &&
      (kind === 'wall' || kind === 'mud' || kind === 'scorpion' || kind === 'tackle')
    ) {
      this.events.push({ type: 'deny', racerId: r.id });
      return;
    }
    // Magnet / Kick / Scorpion all act on YOUR ball — demand it within reach,
    // and (M117) in the same SPACE: a wall between you and your ball is real.
    if (kind === 'magnet' || kind === 'kick' || kind === 'scorpion') {
      const d = Math.hypot(r.car.state.x - r.ball.state.x, r.car.state.z - r.ball.state.z);
      if (d > pw.ballRange || r.ballChannel !== r.carChannel) {
        this.events.push({ type: 'deny', racerId: r.id });
        return; // item retained — get closer to your ball
      }
    }
    // One ghost at a time, and it needs a valid target (the leader — or the
    // next-best racer if the firer somehow leads while holding one).
    let tackleTarget: SimRacer | undefined;
    if (kind === 'tackle') {
      if (this.tackle) {
        this.events.push({ type: 'deny', racerId: r.id });
        return; // a ghost is already out — item retained
      }
      tackleTarget = this.standings().find((s) => s.id !== r.id && !s.finished);
      if (!tackleTarget) return;
    }
    const fired = useItem(r.powerups, pw);
    if (!fired) return;
    switch (fired) {
      case 'boost':
        this.events.push({ type: 'fire', racerId: r.id, kind: 'boost' });
        break;
      case 'magnet':
        this.events.push({ type: 'fire', racerId: r.id, kind: 'magnet' });
        break;
      case 'dozer':
        this.events.push({ type: 'fire', racerId: r.id, kind: 'dozer' });
        break;
      case 'shield':
        this.events.push({ type: 'fire', racerId: r.id, kind: 'shield' });
        break;
      case 'kick':
        kickBall(r.ball.state, r.car.state, pw);
        this.events.push({ type: 'fire', racerId: r.id, kind: 'kick' });
        break;
      case 'tackle': {
        const fp = this.track.project(r.car.state.x, r.car.state.z, r.car.hint);
        this.tackle = {
          firerId: r.id,
          targetId: (tackleTarget as SimRacer).id,
          dist: fp.distance,
          lat: fp.lateral,
          phase: 'chase',
          timer: 0,
          heading: fp.dir,
        };
        this.events.push({
          type: 'tackleLaunch',
          color: r.color,
          targetId: (tackleTarget as SimRacer).id,
        });
        break;
      }
      case 'wall': {
        // FREE KICK WALL (M46): plant it just behind the firer, centred on
        // their lane but clamped so the whole span stays on the road. One
        // standing wall per racer — replanting moves it.
        const pwl = pw.wall;
        const fp = this.track.project(r.car.state.x, r.car.state.z, r.car.hint);
        const dropDist = Math.max(2, fp.distance - pwl.behind);
        const s = this.track.sampleAt(dropDist);
        const maxLat = Math.max(0, this.track.halfWidth - pwl.halfSpan);
        const lat = Math.max(-maxLat, Math.min(maxLat, fp.lateral));
        const prev = this.walls.findIndex((w) => w.ownerId === r.id);
        if (prev >= 0) this.walls.splice(prev, 1);
        this.walls.push({
          ownerId: r.id,
          color: r.color,
          x: s.x + s.rightX * lat,
          y: s.y,
          z: s.z + s.rightZ * lat,
          dirX: Math.sin(s.dir), // §6.1: heading → forward (sin h, cos h)
          dirZ: Math.cos(s.dir),
          rightX: s.rightX,
          rightZ: s.rightZ,
          halfSpan: pwl.halfSpan,
          timer: pwl.life,
        });
        this.events.push({ type: 'fire', racerId: r.id, kind: 'wall' });
        break;
      }
      case 'offside': {
        // OFFSIDE TRAP (M46): the flag goes up — every unshielded racer AHEAD
        // of the firer (by standings, the thing that actually matters) spins
        // out briefly. Shields block it; a Pibe de Oro runner is immune.
        const order = this.standings();
        const myIdx = order.findIndex((s) => s.id === r.id);
        for (let i = 0; i < myIdx; i++) {
          const v = order[i];
          if (v.finished) continue;
          if (v.carChannel >= 0) continue; // M117: inside a channel — the flag can't reach them
          if (isActive(v.powerups, 'shield') || isActive(v.powerups, 'pibe')) continue;
          v.stunTimer = pw.offside.stun;
          // Alternate the spin direction down the field (deterministic).
          v.stunSteer = (i % 2 === 0 ? 1 : -1) * pw.dozerSpinSteer;
          this.events.push({ type: 'offsideHit', victimId: v.id });
        }
        this.events.push({ type: 'fire', racerId: r.id, kind: 'offside' });
        break;
      }
      case 'pibe':
        // PIBE DE ORO (M46): the effects are continuous while active — boost
        // engine (step), magnet cradle (applyMagnets), smash-through
        // (resolveCarPair / pibeBallPush / applyWalls). This just lights it.
        this.events.push({ type: 'fire', racerId: r.id, kind: 'pibe' });
        break;
      case 'studs':
        // STUDS OUT (M59): continuous while active — the car-spin lives in
        // resolveCarPair, the ball-deflect in applyStuds. This just lights it.
        this.events.push({ type: 'fire', racerId: r.id, kind: 'studs' });
        break;
      case 'scorpion': {
        // SCORPION KICK (M59/M60b): a TWO-PHASE bicycle kick. Phase 1 — pop the
        // ball STRAIGHT UP directly above the car (no forward yet). Phase 2 —
        // when the ball reaches its apex (after kickUpTime, detected in the
        // freeze branch of step()), the car leaps + flips and VOLLEYS the ball
        // high + far down-course. The ball is untouchable the whole time
        // (scorpionBallTimer) and lands ~300 u ahead. The car is locked for
        // kickUpTime + flipTime, then drives to chase.
        const sc = pw.scorpion;
        const b = r.ball.state;
        b.vx = 0; // straight up — no forward until the apex volley
        b.vz = 0;
        b.vy = sc.kickUp;
        r.scorpionTimer = sc.kickUpTime + sc.flipTime;
        r.scorpionBallTimer = sc.airTime;
        this.events.push({ type: 'fire', racerId: r.id, kind: 'scorpion' });
        break;
      }
      case 'mud': {
        // SLIPPERY MUD (M59; placement fixed M99): pour the puddle DIRECTLY
        // behind the firer's tail — at the car's actual position, along its
        // heading. (The old code sampled the track CENTRELINE at the car's
        // arc, which dumped every puddle mid-road no matter what lane you
        // poured from — the owner's report.) One puddle per owner —
        // replanting moves it (like the free-kick wall).
        const pm = pw.mud;
        const c = r.car.state;
        const mx = c.x - Math.sin(c.heading) * pm.behind;
        const mz = c.z - Math.cos(c.heading) * pm.behind;
        const prev = this.mud.findIndex((m) => m.ownerId === r.id);
        if (prev >= 0) this.mud.splice(prev, 1);
        this.mud.push({
          ownerId: r.id,
          x: mx,
          y: this.track.heightAt(mx, mz),
          z: mz,
          radius: pm.radius,
          color: r.color,
          timer: pw.wall.life,
        });
        this.events.push({ type: 'fire', racerId: r.id, kind: 'mud' });
        break;
      }
    }
  }

  /** Tick box respawns and let every racer with an empty slot grab one. */
  private updatePickups(dt: number): void {
    stepPickups(this.pickups, dt);
    let standings: SimRacer[] | null = null; // computed lazily (collects are rare)
    let playerDist: number | null = null;
    for (const r of this.racers) {
      if (r.finished || r.carChannel >= 0 || !canCollect(r.powerups)) continue; // (M117: boxes are road furniture — no wall-piercing grabs from inside a bore)
      const idx = tryCollect(
        this.pickups,
        r.car.state.x,
        r.car.state.z,
        tuning.powerups.collectRadius,
        tuning.powerups.respawnTime,
      );
      if (idx < 0) continue;
      standings ??= this.standings();
      const actualPlace = standings.indexOf(r) + 1; // real place BEFORE the desperation forcing
      let place = actualPlace;
      // Desperation roll (M16): anyone dropped far behind the LEAD HUMAN rolls
      // from the tail tier regardless of place — the comeback kit is how a
      // breakaway gets hunted once raw pace saturates. Offline the reference is
      // the player and the player's own gap is 0, so this never fires for them
      // (identical to the old !isPlayer guard); online a trailing HUMAN gets
      // the same comeback kit a trailing rival does (fair across slots, M45).
      // M51: the same "far off the pack" signal also raises the PIBE DE ORO
      // odds for the last two places (pibe.weightDesperate).
      playerDist ??= this.paceReference();
      const farBehind =
        playerDist - r.ballDistance(this.track) > tuning.powerups.desperationGap;
      if (farBehind) place = this.racers.length;
      // SCORPION KICK (M59) is banned in the final stretch — gate by where the
      // roller's CAR physically is on the course.
      const sc = tuning.powerups.scorpion;
      const carDist = this.track.project(r.car.state.x, r.car.state.z, r.car.hint).distance;
      const scorpionOk = carDist < sc.maxCourseFrac * this.track.finishDistance;
      // M60d (owner): the per-match cap folds into the course gate — a racer who
      // already has SCORPION_MAX can never roll another.
      const scorpionAllowed = scorpionOk && r.scorpionCount < SCORPION_MAX;
      // …and a DEAD-LAST racer who is further behind the pack (its nearest rival
      // ahead) than a scorpion throw is AUTO-GRANTED one as their next item.
      const isLast = actualPlace === this.racers.length;
      let gapToPack = 0;
      if (isLast && this.racers.length >= 2) {
        gapToPack =
          standings[this.racers.length - 2].ballDistance(this.track) - r.ballDistance(this.track);
      }
      // One RNG draw per collect (stable stream whether or not the auto-grant
      // fires), then the auto-grant overrides the rolled item when it applies.
      const roll = this.itemRng.next();
      const auto = autoGrantScorpion(
        isLast,
        gapToPack,
        sc.autoGrantGap,
        r.scorpionCount,
        scorpionOk,
        this.racers.length,
      );
      const kind: PowerupKind = auto
        ? 'scorpion'
        : rollItem(roll, place, this.racers.length, tuning.powerups, farBehind, scorpionAllowed);
      if (kind === 'scorpion') r.scorpionCount++;
      collectItem(r.powerups, kind, tuning.powerups);
      this.events.push({ type: 'collect', racerId: r.id });
    }
  }

  private resolveCollisions(dt: number): void {
    const rs = this.racers;
    // Every ball collides with every car (own car = dribble; others = chaos).
    // We watch EVERY ball's velocity delta and emit a ballHit (each client plays
    // only its own racer's thump) — offline that's just the player's ball.
    for (const b of rs) {
      if (b.scorpionBallTimer > 0) continue; // M60: a flying scorpion ball is untouchable
      const preVx = b.ball.state.vx;
      const preVz = b.ball.state.vz;
      // The human's full-speed strikes launch the ball further ahead (M39 buff
      // + M51 vehicle heft); every AI car pushes with the tuned ball, so the
      // difficulty ladder is untouched. A PIBE DE ORO runner (M46) BLASTS
      // rival balls out of its path instead.
      for (const c of rs) {
        if (c.scorpionTimer > 0) continue; // M59: a flipping car is intangible
        if (c.carChannel !== b.ballChannel) continue; // M117: different SPACES (road vs a channel) never touch — same channel duels normally
        // M67 (owner): a STUDS-OUT car sends any rival ball it touches straight to
        // the near guardrail (applyStuds, run after this pass). Skip the normal
        // dribble/strike for that pair here — a hard nose contact would launch the
        // ball forward + UP before the deflect can ground it (the "into the sky,
        // far ahead" bug). The spiker still dribbles ITS OWN ball (c === b).
        if (c !== b && !c.finished && isActive(c.powerups, 'studs')) continue;
        // M143c: a FINISHED rival's car is out of the race and no longer touches
        // the balls still in it. Finished cars coast to rest wherever they were
        // when their ball crossed, which can be right ON the line; a parked car
        // returns a fast ball at most of its speed (measured in Rio: a ball
        // 0.3 short of the finish thrown back three times, then 200 units
        // up-course, and that car never finished). Read off the synced
        // `finished` flag, so every client's prediction agrees.
        if (c !== b && c.finished) continue;
        const p =
          c !== b && isActive(c.powerups, 'pibe')
            ? this.pibeBallPush
            : c.isBot
              ? botBallPushFor(this.difficulty) // M77: Legend strikes like the human (1.5×); other levels = tuning.ball verbatim
              : vehicleBallPushFor(c.vehicleId);
        resolveBallCar(b.ball.state, c.car.state, p, dt, vehicleBoxFor(c.vehicleId)); // M153: the pusher's own box
      }
      const dv = Math.hypot(b.ball.state.vx - preVx, b.ball.state.vz - preVz);
      if (dv > 7) {
        this.events.push({ type: 'ballHit', racerId: b.id, intensity: Math.min(1, dv / 28) });
      }
    }
    // Car-car (bulldozer launch / shield block) and ball-ball pairs.
    for (let i = 0; i < rs.length; i++) {
      for (let j = i + 1; j < rs.length; j++) {
        this.resolveCarPair(rs[i], rs[j]);
        // M60: skip ball-ball while either ball is a flying scorpion kick.
        // M117: …or the two balls sit in different spaces (road vs channel).
        if (
          rs[i].scorpionBallTimer <= 0 &&
          rs[j].scorpionBallTimer <= 0 &&
          rs[i].ballChannel === rs[j].ballChannel
        ) {
          resolveBallBall(rs[i].ball.state, rs[j].ball.state);
        }
      }
    }
  }

  /** Car-vs-car: a bulldozer-active car launches + stuns the rival it rams —
   *  unless the rival's SHIELD is up, in which case the ram clinks off and both
   *  just bump (the shielded car barely budges). Plain contacts bump normally.
   *  PIBE DE ORO (M46) tops everything: the golden runner smashes through ANY
   *  car in its way — shields included — flinging it aside with a spin-out. */
  private resolveCarPair(a: SimRacer, b: SimRacer): void {
    if (a.scorpionTimer > 0 || b.scorpionTimer > 0) return; // M59: flipping = intangible
    if (a.carChannel !== b.carChannel) return; // M117: different spaces never touch (same channel duels normally)
    const aPibe = isActive(a.powerups, 'pibe') && !a.finished;
    const bPibe = isActive(b.powerups, 'pibe') && !b.finished;
    if (aPibe || bPibe) {
      if (aPibe && !bPibe && !b.finished) this.pibeSmash(a, b);
      else if (bPibe && !aPibe && !a.finished) this.pibeSmash(b, a);
      // Two golden runners just bump like normal cars (nobody outranks gold).
      else resolveCarCar(a.car.state, b.car.state, 0.3, 1, 1);
      return;
    }
    const aShield = isActive(a.powerups, 'shield');
    const bShield = isActive(b.powerups, 'shield');
    const aRams = isActive(a.powerups, 'dozer') && !a.finished && !b.finished;
    const bRams = isActive(b.powerups, 'dozer') && !b.finished && !a.finished;
    // STUDS OUT (M59): a spiked car spins out any rival it touches (shield
    // blocks the spin, same doctrine as the bulldozer). A car has at most one
    // active effect, so dozer + studs are mutually exclusive.
    const aStuds = isActive(a.powerups, 'studs') && !a.finished && !b.finished;
    const bStuds = isActive(b.powerups, 'studs') && !b.finished && !a.finished;

    if (aRams && !bShield) this.bulldoze(a, b);
    else if (aStuds && !bShield) this.studsHit(a, b);
    if (bRams && !aShield) this.bulldoze(b, a);
    else if (bStuds && !aShield) this.studsHit(b, a);

    const aHit = (aRams || aStuds) && !bShield;
    const bHit = (bRams || bStuds) && !aShield;
    // No successful ram/spike in either direction → a plain (shield-weighted)
    // bump. M49: a heavier vehicle acts heavier — its shove factor shrinks by
    // 1/weight (a shielded car stays the heaviest thing on the road).
    if (!aHit && !bHit) {
      // M57: judge the weight-clash BEFORE the bump (pre-impulse velocities).
      // Finished racers are parked furniture — they neither spin nor cause it.
      const clash =
        a.finished || b.finished
          ? null
          : resolveCarClash(a.car.state, b.car.state, this.weightOf(a), this.weightOf(b), tuning.clash);
      const f = tuning.powerups.shieldBumpFactor;
      // On a clash the heavy car plows through — its shove share shrinks
      // further, so it keeps its momentum while the light one takes the swat.
      const plow = tuning.clash.heavyPlow;
      const touched = resolveCarCar(
        a.car.state,
        b.car.state,
        0.3,
        ((aShield ? f : 1) / this.weightOf(a)) * (clash?.victim === 'b' ? plow : 1),
        ((bShield ? f : 1) / this.weightOf(b)) * (clash?.victim === 'a' ? plow : 1),
      );
      if (touched && clash) this.applyClash(clash.victim === 'a' ? a : b, clash);
      // A dozer ram / spike hit blocked by a shield gets a (throttled) clink.
      if (touched && (aRams || bRams || aStuds || bStuds) && this.raceTime - this.lastShieldBlock > 0.3) {
        this.lastShieldBlock = this.raceTime;
        this.events.push({ type: 'shieldBlock' });
      }
    }
  }

  /** M57: the LIGHTER car loses composure in a heavy-vs-light hit — a full
   *  spin-out at truck-vs-bike disparity, a brief stagger at the middle tier.
   *  SHIELD blocks the stun but not the shove (the M51 pibe rule); an already-
   *  stunned victim never re-locks (no chain-stunning while sliding along). */
  private applyClash(victim: SimRacer, hit: ClashHit): void {
    if (victim.stunTimer > 0) return;
    if (isActive(victim.powerups, 'shield')) return;
    const c = tuning.clash;
    const spin = hit.tier === 'spin';
    victim.stunTimer = spin ? c.spinStun : c.staggerStun;
    victim.stunSteer =
      hit.spin * tuning.powerups.dozerSpinSteer * (spin ? 1 : c.staggerSteerScale);
    this.events.push({ type: 'clash', victimId: victim.id, tier: hit.tier });
  }

  /** Apply one bulldozer hit: fling the victim and set its spin-out stun. */
  private bulldoze(rammer: SimRacer, victim: SimRacer): void {
    const hit = resolveDozerHit(rammer.car.state, victim.car.state, tuning.powerups);
    if (!hit) return;
    victim.stunTimer = tuning.powerups.dozerStun;
    victim.stunSteer = hit.spin * tuning.powerups.dozerSpinSteer;
    this.events.push({ type: 'dozerHit', victimId: victim.id });
  }

  /** STUDS OUT hit (M59): a spiked car spins out the rival it touches (its own
   *  gentler force/stun than the bulldozer). An already-stunned car can't be
   *  re-locked (no chain-stunning while it slides). */
  private studsHit(rammer: SimRacer, victim: SimRacer): void {
    if (victim.stunTimer > 0) return;
    const st = tuning.powerups.studs;
    const hit = resolveDozerHit(rammer.car.state, victim.car.state, { dozerLaunchForce: st.launchForce });
    if (!hit) return;
    victim.stunTimer = st.stun;
    victim.stunSteer = hit.spin * tuning.powerups.dozerSpinSteer;
    this.events.push({ type: 'studsHit', victimId: victim.id });
  }

  /** STUDS OUT ball-deflect (M59): a spiked car that touches a RIVAL ball fires
   *  it sideways toward the near guardrail (keeping the owner's line ruined).
   *  Runs after the collision pass so the deflect wins over a plain dribble. */
  private applyStuds(): void {
    const st = tuning.powerups.studs;
    const reach2 = st.ballReach * st.ballReach;
    for (const c of this.racers) {
      if (c.finished || c.scorpionTimer > 0 || !isActive(c.powerups, 'studs')) continue;
      for (const b of this.racers) {
        if (b === c || b.scorpionBallTimer > 0) continue; // flying (M60) balls are untouchable
        if (b.ballChannel !== c.carChannel) continue; // M117: same space only (spikes work inside a shared bore too)
        const ball = b.ball.state;
        const dx = ball.x - c.car.state.x;
        const dz = ball.z - c.car.state.z;
        if (dx * dx + dz * dz > reach2) continue;
        const tr = this.ballTrackOf(b); // the deflect aims at THIS space's near wall
        const proj = tr.project(ball.x, ball.z, b.ball.hint);
        b.ball.hint = proj.index;
        // Fire it toward the NEAR guardrail (the side it already sits on; a
        // dead-centre ball picks the side it's drifting toward — deterministic).
        const sign =
          Math.sign(proj.lateral) || (dx * proj.rightX + dz * proj.rightZ >= 0 ? 1 : -1);
        const preVx = ball.vx;
        const preVz = ball.vz;
        ball.vx = proj.rightX * sign * st.ballDeflect;
        ball.vz = proj.rightZ * sign * st.ballDeflect;
        // M67 (owner): keep it GROUNDED — fire it FLAT at the guardrail, never a
        // vertical pop, so a spiked ball can't sail into the sky. Any residual
        // upward velocity (e.g. from an earlier bounce) is cancelled here.
        ball.vy = 0;
        if (Math.hypot(ball.vx - preVx, ball.vz - preVz) > 7) {
          this.events.push({ type: 'ballHit', racerId: b.id, intensity: 1 });
        }
      }
    }
  }

  /** PIBE DE ORO smash (M46): same fling mechanics as the bulldozer (the golden
   *  runner plows through untouched), with its own force/stun knobs + event.
   *  M51 (owner): a raised SHIELD no longer saves you from the PUSH — you're
   *  still shoved out of the golden runner's path — but it does save you from
   *  the SPIN-OUT (no lost control). */
  private pibeSmash(runner: SimRacer, victim: SimRacer): void {
    const pb = tuning.powerups.pibe;
    const hit = resolveDozerHit(runner.car.state, victim.car.state, {
      dozerLaunchForce: pb.launchForce,
    });
    if (!hit) return;
    if (!isActive(victim.powerups, 'shield')) {
      victim.stunTimer = pb.stun;
      victim.stunSteer = hit.spin * tuning.powerups.dozerSpinSteer;
    }
    this.events.push({ type: 'pibeHit', victimId: victim.id });
  }

  /** POSSESSION TOUCH (M84): every racer — human AND bot — dribbles with a
   *  soft grip on its OWN ball (possessionPull — engages only while genuinely
   *  dribbling; any real knock, hard cut or deliberate strike sheds it, see
   *  collisions.ts). Strength scales with the vehicle's HANDLING stat
   *  (possessionParamsFor). Steps aside whenever something stronger owns the
   *  ball: a live magnet/pibe hard-cradle, a flying scorpion ball; a STUNNED
   *  racer (tackled/dozed/spiked/clashed) has no touch — an item hit strips
   *  possession by rule, not just by physics; rival MUD means lost traction,
   *  so no touch there either. Big Kid humans keep their stronger leash
   *  instead (applyKidAssist) — the two never stack.
   *
   *  DOWN-COURSE ONLY (measured, do not remove): a car driving AGAINST the
   *  course direction gets NO touch. Without this gate, an AI repositioning
   *  to get behind its ball (chase-back staging, scrum approach) gathered the
   *  ball onto its up-course-facing nose — and since every staging point is
   *  derived from the BALL's position, the target retreated forever: the NYC
   *  legendary rival dribbled its own ball backward from dist 197 to the
   *  START WALL at a steady 12 u/s (traced). Turnaround fetches simply run
   *  the plain pre-M84 contact physics until the car faces forward again. */
  private applyPossessionAssist(dt: number): void {
    for (const r of this.racers) {
      if (r.finished || r.scorpionBallTimer > 0 || r.stunTimer > 0) continue;
      if (r.carChannel !== r.ballChannel) continue; // M117: no touch through a bore wall — car and ball must share a space
      if (isActive(r.powerups, 'magnet') || isActive(r.powerups, 'pibe')) continue;
      if (this.difficulty === 'bigkid' && !r.isBot) continue; // the kid leash supersedes
      if (this.carInRivalMud(r)) continue;
      const c = r.car.state;
      // M117: inside a channel the touch reads the CHANNEL's forward — the
      // down-course-only gate generalizes to "down-BORE only" for free.
      const proj = this.carTrackOf(r).project(c.x, c.z, r.car.hint);
      // Course forward from the projection's right vector: right = (cos h, −sin h)
      // for course heading h ⇒ forward = (−rightZ, rightX).
      const fx = Math.sin(c.heading);
      const fz = Math.cos(c.heading);
      if (fx * -proj.rightZ + fz * proj.rightX <= 0) continue;
      possessionPull(r.ball.state, r.car.state, possessionParamsFor(r.vehicleId), dt, vehicleBoxFor(r.vehicleId));
    }
  }

  /** BIG KID ball assist (M83): at the 'bigkid' difficulty every HUMAN's ball
   *  wears a soft always-on leash (kidAssistPull — a capped velocity pull
   *  toward the nose spot, never a pin) so a child keeps possession without
   *  precise driving. Bots never get it (the escort rubber is their half of
   *  the mode); a flying scorpion ball stays untouchable; a live magnet/pibe
   *  hard-cradle owns the ball outright, so the leash steps aside. Offline
   *  only by construction — online sims always run 'legendary' (M76). */
  private applyKidAssist(dt: number): void {
    if (this.difficulty !== 'bigkid') return;
    for (const r of this.racers) {
      if (r.isBot || r.finished || r.scorpionBallTimer > 0) continue;
      if (r.carChannel !== r.ballChannel) continue; // M117: no leash through a bore wall
      if (isActive(r.powerups, 'magnet') || isActive(r.powerups, 'pibe')) continue;
      kidAssistPull(r.ball.state, r.car.state, tuning.kidAssist, dt, vehicleBoxFor(r.vehicleId));
    }
  }

  /** Magnet: after physics + collisions, pin each holder's ball to its nose
   *  (carrying the car's velocity, so it releases with momentum), then sit it on
   *  the ground and keep it inside the guardrails. PIBE DE ORO (M46) gets the
   *  same cradle — but instead of a fire-range gate, the ball LATCHES the moment
   *  the runner gets within ballRange (a star shouldn't be deniable). */
  private applyMagnets(): void {
    for (const r of this.racers) {
      if (r.finished) continue;
      if (r.carChannel !== r.ballChannel) continue; // M117: no cradle through a bore wall
      let hold = isActive(r.powerups, 'magnet');
      if (!hold && isActive(r.powerups, 'pibe')) {
        const d = Math.hypot(r.car.state.x - r.ball.state.x, r.car.state.z - r.ball.state.z);
        hold = d <= tuning.powerups.ballRange;
      }
      if (!hold) continue;
      const ball = r.ball.state;
      magnetHold(ball, r.car.state, tuning.powerups.magnetBallGap, vehicleBoxFor(r.vehicleId));
      const tr = this.ballTrackOf(r); // M117: inside a channel the cradle clamps to the BORE walls
      const proj = tr.project(ball.x, ball.z, r.ball.hint);
      r.ball.hint = proj.index;
      ball.y = proj.y + ball.radius;
      ball.vy = 0;
      const w = resolveWall(
        proj.lateral,
        tr.halfWidth - ball.radius,
        proj.rightX,
        proj.rightZ,
        ball.vx,
        ball.vz,
        tuning.ball.wallRestitution,
      );
      if (w) {
        ball.x += w.pushX;
        ball.z += w.pushZ;
        ball.vx = w.vx;
        ball.vz = w.vz;
      }
    }
  }

  /** Times Square red stairs: a car/ball taking the CENTRE of the road rides up
   *  the ramp and launches off the lip (clear road on both sides to pass). */
  private applyRamp(): void {
    const ramp = this.ramp;
    if (!ramp) return;
    const start = ramp.at * this.track.finishDistance;
    const slope = rampSlope(ramp);
    for (const r of this.racers) {
      if (r.finished) continue;
      if (r.carChannel < 0) {
        // (M117: the city ramp is road furniture — per-entity, like obstacles)
        const c = r.car;
        const cp = this.track.project(c.state.x, c.state.z, c.hint);
        const ch = rampHeight(ramp, start, cp.distance, cp.lateral);
        if (ch > 0) {
          // M117c: when the city ramp IS a skypath launcher, humans riding it
          // collect the launch aids (surge + ball magnet). Bots just jump it
          // as they always have — they can never catch the deck.
          if (!r.isBot && this.shortcuts.some((s) => s.catch)) this.grantLaunchAids(r);
          rideRamp(c, cp.y + ch, Math.hypot(c.state.vx, c.state.vz), slope);
          c.airborne = true;
        }
      }
      if (r.ballChannel < 0) {
        const b = r.ball.state;
        const bp = this.track.project(b.x, b.z, r.ball.hint);
        const bh = rampHeight(ramp, start, bp.distance, bp.lateral);
        if (bh > 0) {
          rideRamp(b, bp.y + b.radius + bh, Math.hypot(b.vx, b.vz), slope);
        }
      }
    }
  }

  private updateFinish(): void {
    for (const r of this.racers) {
      if (r.finished) continue;
      if (r.ballDistance(this.track) >= this.track.finishDistance) {
        r.finished = true;
        r.finishOrder = this.finishCount++;
        r.finishTime = this.raceTime;
        this.events.push({
          type: 'finish',
          racerId: r.id,
          order: r.finishOrder,
          timeMs: Math.round(r.finishTime * 1000),
        });
      }
    }
    // The SIM is "finished" only when EVERY racer is done — so one human crossing
    // doesn't freeze the others mid-race (online). Each CLIENT shows its own
    // results when ITS local player finishes (Game.update); offline, Game stops
    // stepping on the player's finish, so the AI tail never actually runs.
    if (this.racers.every((r) => r.finished)) this.state = 'finished';
  }

  /** Racers ranked: finished (by order) first, then the rest by ball progress. */
  standings(): SimRacer[] {
    return [...this.racers].sort((a, b) => {
      if (a.finished && b.finished) return a.finishOrder - b.finishOrder;
      if (a.finished) return -1;
      if (b.finished) return 1;
      return b.ballDistance(this.track) - a.ballDistance(this.track);
    });
  }

  /** The player's current placement, 1-based. */
  playerPlace(): number {
    return this.standings().findIndex((r) => r.isPlayer) + 1;
  }
}
